/* eslint-disable max-len */
import axios from "axios";
import { useContext, useMemo, useEffect, useState } from "react";
import { useScreenClass } from "react-grid-system";
import { Col, Row } from "../../components/StyledGridSystem";
import { UserContext } from "../../context/UserContext";
import TabGroup, { TabItem } from "../../components/TabGroup";
import { StyledText } from "../../components/Text";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import CompetencyDetail from "./CompetencyDetail";
import { PillarList } from "../Credits/pillarList";
import { CompetencyList } from "../Credits/CompetencyList";
import { handleJWTError } from "../../hooks/jwtErrHandler";
import { SemesterContext } from "../../context/SemesterContext";
import {
  findPillarForCompetency,
  getCardGraphics,
  getCompetencyPillarPrefix,
} from "../../utils/cardGraphics";
function MyCards() {
  const semesterContext = useContext(SemesterContext);
  const screenClass = useScreenClass();
  const [competencies, setCompetencies] = useState<Array<any>>([]);
  const [inProgress, setInProgress] = useState<Array<any>>([]);
  const [completed, setCompleted] = useState<Array<any>>([]);
  const [masteryLevel, setMasteryLevel] = useState<Array<any>>([]);
  const [archived, setArchived] = useState<Array<any>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedCompetency, setSelectedCompetency] = useState<{}>();
  const [progressCredits, setProgressCredits] = useState<string>("0");
    const [archivedCredits, setArchivedCredits] = useState<string>("0");

  const [earnedCredits, setEarnedCredits] = useState<string>("0");
  const [selectedPillar, setSelectedPillar] = useState<any>();
  const [currentVersionPillar, setCurrentVersionPillar] = useState<any>([]);

  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  // const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(0);
  const currentUser = useContext(UserContext).user;
  const navigate = useNavigate();
  useEffect(() => {
    if (!semesterContext.selectedSemester?.year) return; // Wait until semester context is loaded

    fetchAllPillars();
    fetchCredits(currentUser.id);
  }, [currentUser, semesterContext.selectedSemester]);

  const fetchAllPillars = async () => {
    try {
      const versionRes = await axios.get(`/version?university_code=CMKL`);
      const activeVersion = versionRes.data.versions.find(
        (v: any) => v.status === "Active Prime"
      )?.version_number;

      if (!activeVersion) {
        setCurrentVersionPillar([]);
        fetchCompetencies(currentUser.id);
        return;
      }

      const subdomainRes = await axios.get(
        `/subdomain?curriculum_version=${activeVersion}`
      );
      const pillars = subdomainRes.data.pillars ?? [];
      setCurrentVersionPillar(pillars);
      fetchCompetencies(currentUser.id);
    } catch (err) {
      handleJWTError(err);
      setLoading(false);
    }
  };
  const fetchCompetencies = (id: string) => {
    setLoading(true);
    const programName = currentUser?.programs?.[0]?.program_name || "";
    
    axios.get(`/student/cards?student_id=${id}&program_name=${programName}`)
      .then((res) => {
        const mappedCards = res.data.card_info.cards;
        console.log("==> MAPPED CARDS:", mappedCards);

        setInProgress(
          mappedCards.filter(
            (item: { status: string; competency_code: string }) =>
              (item.status === "Selected" || item.status === "In progress" || item.status === "Completed") &&
              !item.competency_code.includes("SOF")
          )
        );
        setMasteryLevel(
          mappedCards.filter(
            (item: { mastery_level_title: string }) =>
              item.mastery_level_title === "Legend" ||
              item.mastery_level_title === "Master" ||
              item.mastery_level_title === "Apprentice" ||
              item.mastery_level_title === "Novice"
          )
        );
        setCompleted(
          mappedCards.filter(
            (item: { status: string; competency_code: string }) =>
              (item.status === "Recorded") &&
              !item.competency_code.includes("SOF")
          )
        );
        setArchived(
          mappedCards.filter(
            (item: { status: string; competency_code: string }) =>
              (item.status === "Archived") &&
              !item.competency_code.includes("SOF")
          )
        );
        setArchivedCredits(
          mappedCards
            .filter((item: { status: string; competency_code: string }) =>
              (item.status === "Archived") &&
              !item.competency_code.includes("SOF")
            )
            .reduce((total: number, item: any) => total + item.credits, 0)
            .toString()
        );
        setCompetencies(
          mappedCards.filter(
            (item: { status: string; competency_code: string }) =>
              (item.status === "Selected" ||
                item.status === "Recorded" ||
                item.status === "Completed" ||
                item.status === "In progress") &&
              !item.competency_code.includes("SOF")
          )
        );
        setLoading(false);
      })
      .catch((err) => {
        handleJWTError(err);

        setLoading(false);
      });
  };
  const fetchCredits = (id: string) => {
    setLoading(true);
    axios
      .get(`/student/credit?student_id=${id}&program_name=${currentUser?.programs?.[0]?.program_name || ""}`)
      .then((res) => {
        setProgressCredits(
          res.data.credit.overall_credit.total_working_credits
        );
        setEarnedCredits(res.data.credit.overall_credit.total_earned_credits);
        setLoading(false);
      })
      .catch((err) => {
        handleJWTError(err);

        setLoading(false);
      });
  };

  const transformData = (cards: any) => {
    // Use a Map to group data by `pillar_title`
    const groupedData = new Map();

    cards.forEach((card: any) => {
      const {
        competency_code,
        title,
        pillar_title,
        id,
        description,
        subdomain_title,
        required,
        credits,
        status_val,
        status,
        mastery_level,
        mastery_level_title,
        transferred,
        earned,
        curriculum_version,
        total_skills,
        assessed_skills,
        skills,
        card_history_events,
        affect_gpa,
        semester_name,
        university_code,
        graphics,
        instructor_approved,
        template_pillar_id,
      } = card;

      const pillar_prefix = getCompetencyPillarPrefix(competency_code);
      const matchedPillar = findPillarForCompetency(
        currentVersionPillar,
        competency_code,
        template_pillar_id
      );

      if (!groupedData.has(pillar_prefix)) {
        groupedData.set(pillar_prefix, {
          pillar_prefix,
          pillar_title: matchedPillar?.title || pillar_title,
          competencies: [],
        });
      }

      groupedData.get(pillar_prefix).competencies.push({
        id,
        pillar_title,
        title,
        competency_code,
        description,
        subdomain_title,
        required,
        credits,
        status_val,
        status,
        mastery_level,
        mastery_level_title,
        transferred,
        earned,
        curriculum_version,
        total_skills,
        assessed_skills,
        skills,
        card_history_events,
        affect_gpa,
        semester_name,
        university_code,
        instructor_approved,
        graphics: getCardGraphics(
          { competency_code, graphics, template_pillar_id },
          currentVersionPillar
        ),
      });
    });

    // Convert the Map values to an array
    return Array.from(groupedData.values());
  };

  const completedPillars = transformData(completed);
  const inProgressPillars = transformData(inProgress);
  const allPillars = transformData(competencies);
  const archivedPillars = transformData(archived);

  if (isModalOpen) {
    return (
      <CompetencyDetail
        stuId={currentUser.id}
        setIsModalOpen={setIsModalOpen}
        selectedCompetency={selectedCompetency}
        tab={tab}
      />
    );
  }
  return (
    <>
      <Row>
        <Col>
          <TabGroup>
            <TabItem
              isMobile={isMobile}
              active={tab === 0}
              onClick={() => {
                setTab(0);
                setSelectedPillar(null);
              }}
            >
              IN PROGRESS ({inProgress.length} CARDS)
            </TabItem>
            <TabItem
              isMobile={isMobile}
              active={tab === 1}
              onClick={() => {
                setTab(1);
                setSelectedPillar(null);
              }}
            >
              EARNED ({completed.length} CARDS)
            </TabItem>
            <TabItem
              isMobile={isMobile}
              active={tab === 3}
              onClick={() => {
                setTab(3);
                setSelectedPillar(null);
              }}
            >
              EARNED & IN PROGRESS ({competencies.length} CARDS)
            </TabItem>
            <TabItem
              isMobile={isMobile}
              active={tab === 2}
              onClick={() => {setTab(2);
                setSelectedPillar(null);}}
            >
              ARCHIVED ({archived.length} CARDS)
            </TabItem>
            {tab === 0 &&
              (isMobile ? (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "0", fontSize: "14px" }}
                >
                  Credits: {progressCredits}
                </StyledText>
              ) : (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "-8px 0 0 12px", fontSize: "20px" }}
                >
                  Credits: {progressCredits}
                </StyledText>
              ))}
            {tab === 1 &&
              (isMobile ? (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "0", fontSize: "14px" }}
                >
                  Credits Earned: {earnedCredits}
                </StyledText>
              ) : (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "-8px 0 0 12px", fontSize: "20px" }}
                >
                  Credits earned: {earnedCredits}
                </StyledText>
              ))}
            {tab === 3 &&
              (isMobile ? (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "0", fontSize: "14px" }}
                >
                  Total Credits:{" "}
                  {parseInt(progressCredits) + parseInt(earnedCredits)}
                </StyledText>
              ) : (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "-8px 0 0 12px", fontSize: "20px" }}
                >
                  Total Credits:{" "}
                  {parseInt(progressCredits) + parseInt(earnedCredits)}
                </StyledText>
              ))}
              {tab === 2 &&
              (isMobile ? (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "0", fontSize: "14px" }}
                >
                  Total Credits:{" "}
                  {parseInt(archivedCredits)}
                </StyledText>
              ) : (
                <StyledText
                  fontWeight="500"
                  style={{ margin: "-8px 0 0 12px", fontSize: "20px" }}
                >
                  Total Credits:{" "}
                  {parseInt(archivedCredits)}
                </StyledText>
              ))}
          </TabGroup>

          {tab === 0 && (
            <>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  {selectedPillar ? (
                    <CompetencyList
                      setSelectedCompetency={setSelectedCompetency}
                      setIsDetailModalOpen={setIsModalOpen}
                      setSelectedPillar={setSelectedPillar}
                      selectedPillar={selectedPillar}
                    />
                  ) : (
                    <PillarList
                      data={inProgressPillars}
                      setSelectedPillar={setSelectedPillar}
                    />
                  )}
                </>
              )}
            </>
          )}
          {tab === 1 && (
            <>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  {selectedPillar ? (
                    <CompetencyList
                      setSelectedCompetency={setSelectedCompetency}
                      setIsDetailModalOpen={setIsModalOpen}
                      setSelectedPillar={setSelectedPillar}
                      selectedPillar={selectedPillar}
                    />
                  ) : (
                    <PillarList
                      data={completedPillars}
                      setSelectedPillar={setSelectedPillar}
                    />
                  )}
                </>
              )}
            </>
          )}

          {tab === 2 && (
            <>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  {selectedPillar ? (
                    <CompetencyList
                      setSelectedCompetency={setSelectedCompetency}
                      setIsDetailModalOpen={setIsModalOpen}
                      setSelectedPillar={setSelectedPillar}
                      selectedPillar={selectedPillar}
                    />
                  ) : (
                    <PillarList
                      data={archivedPillars}
                      setSelectedPillar={setSelectedPillar}
                    />
                  )}
                </>
              )}
            </>
          )}

          {tab === 3 && (
            <>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  {selectedPillar ? (
                    <CompetencyList
                      setSelectedCompetency={setSelectedCompetency}
                      setIsDetailModalOpen={setIsModalOpen}
                      setSelectedPillar={setSelectedPillar}
                      selectedPillar={selectedPillar}
                    />
                  ) : (
                    <PillarList
                      data={allPillars}
                      setSelectedPillar={setSelectedPillar}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </>
  );
}
export default MyCards;
